// Benchmarking Workflow - Main Container
// 3-step guided cell selection process

import { h } from 'preact'
import { useEffect } from 'preact/hooks'
import { lazy, Suspense } from 'preact/compat'
import { useLocation } from 'wouter-preact'
import {
  currentStep,
  workflowAllCells,
  fundamentalsCells,
  performanceCells,
  performanceCellsMeetingRequirements,
  workflowDccEvaluationComplete,
  workflowDccEvaluatedCellCount,
  restoreWorkflowState,
  resetWorkflow,
  selectWorkflowPool
} from '../../state/benchmarkingWorkflowState.js'
import { allCells, sidebarVisible } from '../../state/globalSignals.js'
import { isDemoCatalogUnlocked } from '../../state/demoLimitStore.js'
import { compareCells } from '../../state/filterStore.js'
import { preloadDccData } from '../../utils/dccDataLoader.js'
import WorkflowNavigation from './WorkflowNavigation.jsx'
import { IconRefresh } from '../shared/Icons.jsx'
import { hasSeenTour, showTourPrompt } from '../../tours/tourState.js'
import TourPrompt from '../../tours/TourPrompt.jsx'

// Lazy load step components
const Step1Fundamentals = lazy(() => import('./Step1Fundamentals.jsx'))
const Step2Performance = lazy(() => import('./Step2Performance.jsx'))
const Step3CellList = lazy(() => import('./Step3CellList.jsx'))

export default function BenchmarkingWorkflow() {
  const [location, setLocation] = useLocation()
  
  // Initialize: Load cells and restore state
  useEffect(() => {
    // Collapse sidebar for Steps 1-3 (using CSS)
    const sidebar = document.querySelector('.adaptive-sidebar')
    if (sidebar) {
      sidebar.setAttribute('data-workflow-mode', 'active')
    }
    
    // Save previous sidebar state to restore on unmount
    const previousSidebarState = sidebarVisible.value
    
    // Restore workflow state from localStorage
    restoreWorkflowState()
    
    // Sync URL with current step
    const stepRoutes = {
      1: 'fundamentals',
      2: 'performance',
      3: 'cells'
    }
    const currentRoute = stepRoutes[currentStep.value]
    const expectedPath = `/cell-selection?step=${currentRoute}`
    if (location !== expectedPath) {
      setLocation(expectedPath)
    }
    
    // Restore sidebar on unmount
    return () => {
      const sidebar = document.querySelector('.adaptive-sidebar')
      if (sidebar) {
        sidebar.removeAttribute('data-workflow-mode')
        sidebar.classList.remove('workflow-hidden', 'workflow-visible', 'adaptive-sidebar--collapsed')
      }
      sidebarVisible.value = previousSidebarState
    }
  }, [])
  
  // Copy the candidate pool to workflow state AND start preloading DCC data.
  //
  // Pool selection is DEMO-aware (mirrors MainAppPage's isDemoCatalogUnlocked
  // exception): DEMO keeps the locked shadow cells, which carry base KPIs and
  // are shown across the app — otherwise the wizard collapses to the ~30
  // unlocked cells. Free-tier still excludes locked cells (no usable data).
  // Shadows flow through the steps as shadows: preloadDccData tolerates cells
  // without DCC curves, fundamentalsCellsWithDcc only surfaces cells that
  // actually loaded DCC data, and scoring falls back to 0 for missing KPIs — so
  // shadows appear (fundamentals/list) without being fully scorable, consistent
  // with the explorer/Ragone plot.
  useEffect(() => {
    if (allCells.value && allCells.value.length > 0) {
      const pool = selectWorkflowPool(allCells.value, isDemoCatalogUnlocked())
      console.log('[BenchmarkingWorkflow] Loading cells:', pool.length, '(from', allCells.value.length, 'total, demo:', isDemoCatalogUnlocked(), ')')
      workflowAllCells.value = pool

      // Start preloading DCC data in background while user is at Step 1
      // This ensures data is ready when user reaches My Plot view
      console.log('[BenchmarkingWorkflow] Starting background DCC data preload...')
      preloadDccData(pool)
    }
  }, [allCells.value])
  
  // Show tour prompt for first-time visitors - DISABLED (feature not yet mature)
  /*
  useEffect(() => {
    if (!hasSeenTour('benchmarking')) {
      // Delay to ensure page is fully rendered
      const timer = setTimeout(() => {
        showTourPrompt.value = true
      }, 800)
      return () => clearTimeout(timer)
    }
  }, [])
  */
  
  // Expose currentStep to window for tour navigation (avoids circular dependencies)
  useEffect(() => {
    window.__BENCHMARKING_WORKFLOW__ = { currentStep }
    console.log('[BenchmarkingWorkflow] Exposed workflow state to window')
    return () => {
      delete window.__BENCHMARKING_WORKFLOW__
      console.log('[BenchmarkingWorkflow] Cleaned up window workflow state')
    }
  }, [])
  
  // Sync step with URL
  useEffect(() => {
    const stepMap = {
      'fundamentals': 1,
      'performance': 2,
      'cells': 3
    }
    
    const stepParam = new URLSearchParams(window.location.search).get('step')
    if (stepParam) {
      const step = stepMap[stepParam]
      if (step && step !== currentStep.value) {
        currentStep.value = step
      }
    }
  }, [location])
  
  // Control sidebar visibility based on step (Steps 1-2: collapsed, Step 3: collapsed until cells added to compare)
  useEffect(() => {
    const sidebar = document.querySelector('.adaptive-sidebar')
    if (!sidebar) return
    
    // Always collapse sidebar in workflow mode - it opens when cells are added to compare
    sidebar.classList.add('workflow-hidden', 'adaptive-sidebar--collapsed')
    sidebar.classList.remove('workflow-visible')
    console.log(`[Workflow] Step ${currentStep.value}: Sidebar collapsed (opens when cells added to compare)`)
  }, [currentStep.value])
  
  // Get current step component
  const getCurrentStepComponent = () => {
    switch (currentStep.value) {
      case 1:
        return <Step1Fundamentals />
      case 2:
        return <Step2Performance />
      case 3:
        return <Step3CellList />
      default:
        return <Step1Fundamentals />
    }
  }
  
  // Get cell counts for each step
  const cellCounts = {
    total: workflowAllCells.value?.length || 0,
    fundamentals: fundamentalsCells.value?.length || 0,
    performance: performanceCellsMeetingRequirements.value?.length > 0
      ? performanceCellsMeetingRequirements.value.length
      : (workflowDccEvaluationComplete.value && workflowDccEvaluatedCellCount.value === 0
          ? fundamentalsCells.value?.length || 0
          : 0),
    cells: compareCells.value?.length || 0  // Cells added to compare
  }
  
  return (
    <div className="benchmarking-workflow benchmarking-main" data-tour="benchmarking-container">
      {/* Tour Prompt disabled - feature not yet mature
      <TourPrompt tourId="benchmarking" />
      */}
      
      {/* Step Content */}
      <div className="workflow-content">
        <Suspense fallback={
          <div className="workflow-loading">
            <div className="loading-spinner">Loading...</div>
          </div>
        }>
          {getCurrentStepComponent()}
        </Suspense>
      </div>
      
      {/* Navigation Footer */}
      <WorkflowNavigation cellCounts={cellCounts} />
    </div>
  )
}
